/**
 * What the reader does not say.
 *
 * **Ruling 82**, numbered on 17 October 2026 (a) section 9 and held in doc 03
 * section 4.5.1. Its four rules:
 *
 * > "In everything the reader speaks:
 * >
 * > - Abbreviations are spoken in full. 'C Sqn' is 'C Squadron'. 'Sqn O Gp' is
 * >   'Squadron O Group'. 'Tp Ldrs' is 'troop leaders'.
 * > - List labels, lettered or numbered, are never spoken. For 'b. No move
 * >   before <time>', say only 'No move before <time>'. This holds in every
 * >   paragraph, admin instructions included.
 * > - '(x2)' means say it twice. It is never spoken.
 * > - Dashes and other separator runs, such as '---', are never spoken. Say
 * >   nothing."
 *
 * The rules were built from the quoted text in block 16 October 2026 (a),
 * whose header and first nine sections never arrived, so they ran unnumbered
 * for a day.
 *
 * Every rule here touches the **spoken** text only. The orders pane stays
 * source-faithful, because the trainee reads along with it.
 */

/**
 * A list label at the start of what is about to be spoken.
 *
 * A single letter, a roman numeral or a number, followed by a stop or a
 * bracket. Deliberately not "one to four letters": that form strips "No. 3"
 * down to "3", and a rule that quietly eats a word is worse than a label
 * spoken aloud.
 */
const LIST_LABEL = /^\s*\(?(?:[A-Za-z]|[IVXivx]{1,5}|\d{1,2})[.)]\s+/;

/** A line that is nothing but a run of dashes. */
const SEPARATOR_LINE = /^[\s]*[-–—]{3,}[\s]*$/;

/** The marker that means say it twice. */
const SAYS_TWICE = /\(\s*x\s*2\s*\)/i;

/**
 * Strips a leading list label.
 *
 * His item 19 of 14 October 2026: "dont say the a./b./c. letter". Item 21
 * asks for the same under the administration instructions, which is the same
 * rule and not a second one.
 *
 * Numbered labels are stripped too. The warning order is a single flat list,
 * so its `1.` to `4.` are list labels in exactly the way its `a.` to `h.`
 * are, and leaving half of them spoken would answer half the complaint.
 */
export function withoutListLabel(text: string): string {
  return text.replace(LIST_LABEL, "");
}

/**
 * Removes separator lines.
 *
 * His item 23: "dont say dash at the end, say nothing". Say nothing is not
 * the word "dash" and not a pause: the line leaves the spoken stream, and an
 * item that was only a separator yields no segment at all, because
 * `splitIntoChunks` returns nothing for an empty string.
 */
export function withoutSeparatorRuns(text: string): string {
  const lines = text.split("\n");
  const kept = lines.filter((line) => !SEPARATOR_LINE.test(line));
  // Text with no separator in it is returned untouched rather than trimmed.
  // A rule that also quietly tidies whitespace everywhere it was not written
  // for is a rule whose effects cannot be read from its name.
  if (kept.length === lines.length) return text;
  return kept.join("\n").trim();
}

/** Whether this text is marked to be said twice. */
export function saysTwice(text: string): boolean {
  return SAYS_TWICE.test(text);
}

/**
 * Removes the marker, leaving the words it marked.
 *
 * His item 18: "dont say (x2), say the probable mission twice". The saying
 * twice happens in the schedule, which is the only place that can add a
 * second segment; this leaves the text sayable.
 */
export function withoutSaysTwiceMarker(text: string): string {
  if (!SAYS_TWICE.test(text)) return text;
  return text.replace(new RegExp(SAYS_TWICE.source, "gi"), "").trim();
}

/**
 * The plural of a one-word expansion.
 *
 * The block states the rule — "Abbreviations are spoken in full" — and gives
 * `Tps` as "troops" and `Ldrs` as "leaders" beside their singulars. This
 * derives the same answers from the singular entries rather than requiring
 * every plural to be written out, which is why `Sqn` worked for weeks while
 * `Sqns` was spoken as written: the lexicon holds singulars and the matcher
 * is word-bounded, so nothing inherited its singular's expansion.
 *
 * **One-word expansions, or a letters form.** Ruling 94 of 17 October 2026 (c)
 * added the second: "An abbreviation made of initial letters is said as
 * letters... Its plural adds 's' to the last letter: 'S O Ps'." That rule
 * covers every entry the ruling turned into letters, so each derives its own
 * plural instead of waiting to be written out.
 *
 * Anything else is still left alone and reported. A multi-word expansion
 * cannot be pluralised mechanically — `Wng O` is "warning order", and
 * pluralising the head noun of a doctrinal phrase is a reading, not a
 * transformation.
 */
export function pluralOfSpoken(spoken: string): string | null {
  if (/^[A-Z](?: [A-Z])+$/.test(spoken)) return `${spoken}s`;
  if (!/^[A-Za-z]+$/.test(spoken)) return null;
  if (/(?:s|x|z|ch|sh)$/i.test(spoken)) return `${spoken}es`;
  if (/[^AEIOUaeiou]y$/.test(spoken)) return `${spoken.slice(0, -1)}ies`;
  return `${spoken}s`;
}
